import Risksystem3PublicAuthService from './risksystem3-public-auth-service';
import TokenService from './token-service';
import { BroadcastChannel } from 'broadcast-channel';
import jwt_decode from 'jwt-decode';

const AUTH_STORAGE_UPDATED_EVENT = 'auth:storage-updated';
const BROADCAST_CHANNEL_NAME = 'raptor-auth';
const MAX_TIMEOUT_MS = 2147483647;
type LogoutReason = 'session_expired' | 'inactivity' | 'manual_logout';

interface LogoutMessage {
  type: 'logout';
  reason?: LogoutReason;
}

interface JwtPayload {
  exp?: number;
}

let sessionExpiryTimer: number | null = null;
let broadcastChannel: BroadcastChannel<LogoutMessage> | null = null;
let hasInitialised = false;

const decodeJwtExpMs = (token?: string | null): number | null => {
  if (!token) return null;
  try {
    const payload = jwt_decode<JwtPayload>(token);
    const exp = payload?.exp;
    if (!exp || typeof exp !== 'number') return null;
    return exp * 1000;
  } catch {
    return null;
  }
};

const getSessionExpiryMs = (): number | null => {
  const user = TokenService.getUser();
  if (!user) return null;

  const refreshTokenExp = decodeJwtExpMs(user.refresh_token);
  if (refreshTokenExp) return refreshTokenExp;

  const accessTokenExp =
    decodeJwtExpMs(user.access_token) ||
    decodeJwtExpMs(TokenService.getLocalAccessToken());
  if (accessTokenExp) return accessTokenExp;

  const expires = Number((user as any).expires);
  if (!expires || Number.isNaN(expires)) return null;

  if (expires > 1e12) return expires;
  if (expires > 1e9) return expires * 1000;
  if (expires > 0) return Date.now() + expires * 1000;

  return null;
};

const isAuthenticatedLocally = (): boolean =>
  localStorage.getItem('authenticated') === 'true' &&
  !!TokenService.getUser() &&
  !!TokenService.getLocalRefreshToken();

const clearSessionExpiryTimer = () => {
  if (sessionExpiryTimer) {
    window.clearTimeout(sessionExpiryTimer);
    sessionExpiryTimer = null;
  }
};

const triggerSessionExpiredLogout = () => {
  if (!isAuthenticatedLocally()) return;
  Risksystem3PublicAuthService.FastAPILogout('session_expired');
};

const scheduleSessionExpiryCheck = () => {
  clearSessionExpiryTimer();

  if (!isAuthenticatedLocally()) return;
  const expiryMs = getSessionExpiryMs();
  if (!expiryMs) return;

  const msUntilExpiry = expiryMs - Date.now();
  if (msUntilExpiry <= 0) {
    triggerSessionExpiredLogout();
    return;
  }

  const nextDelay = Math.min(msUntilExpiry, MAX_TIMEOUT_MS);
  sessionExpiryTimer = window.setTimeout(() => {
    scheduleSessionExpiryCheck();
  }, nextDelay);
};

const toLogoutReason = (reason?: string): LogoutReason => {
  if (
    reason === 'session_expired' ||
    reason === 'inactivity' ||
    reason === 'manual_logout'
  ) {
    return reason;
  }
  return 'session_expired';
};

const handleCrossTabLogout = (reason?: string) => {
  Risksystem3PublicAuthService.FastAPILogout(toLogoutReason(reason), {
    broadcast: false,
  });
};

const onBroadcastMessage = (msg: LogoutMessage) => {
  if (msg?.type === 'logout') {
    handleCrossTabLogout(msg.reason);
  }
};

const onStorageEvent = (event: StorageEvent) => {
  if (event.key === 'user' || event.key === 'access_token' || event.key === 'authenticated') {
    scheduleSessionExpiryCheck();
  }
};

const onVisibilityOrFocus = () => {
  if (document.visibilityState === 'visible') {
    scheduleSessionExpiryCheck();
  }
};

const onAuthStorageUpdated = () => {
  scheduleSessionExpiryCheck();
};

export const initialiseAuthSessionManager = () => {
  if (hasInitialised) return;
  hasInitialised = true;

  if (
    localStorage.getItem('authenticated') === 'true' &&
    !TokenService.getLocalRefreshToken()
  ) {
    Risksystem3PublicAuthService.FastAPILogout('session_expired', {
      broadcast: false,
    });
    return;
  }

  broadcastChannel = new BroadcastChannel<LogoutMessage>(BROADCAST_CHANNEL_NAME);
  broadcastChannel.onmessage = onBroadcastMessage;

  window.addEventListener('storage', onStorageEvent);
  window.addEventListener('focus', onVisibilityOrFocus);
  document.addEventListener('visibilitychange', onVisibilityOrFocus);
  window.addEventListener(AUTH_STORAGE_UPDATED_EVENT, onAuthStorageUpdated);

  scheduleSessionExpiryCheck();
};

export const shutdownAuthSessionManager = () => {
  clearSessionExpiryTimer();
  window.removeEventListener('storage', onStorageEvent);
  window.removeEventListener('focus', onVisibilityOrFocus);
  document.removeEventListener('visibilitychange', onVisibilityOrFocus);
  window.removeEventListener(AUTH_STORAGE_UPDATED_EVENT, onAuthStorageUpdated);

  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  hasInitialised = false;
};


